<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import WhatsappTemplatesModal from 'dashboard/components/widgets/conversation/WhatsappTemplates/Modal.vue';

const store = useStore();
const emit = defineEmits(['close']);

const visible = ref(true);
const step = ref(1);
const searchQuery = ref('');
const searchResults = ref([]);
const searchLoading = ref(false);
const selectedContact = ref(null);
const selectedInbox = ref(null);
const inboxes = ref([]);
const messageText = ref('');
const sending = ref(false);
const debounceTimer = ref(null);
const phoneCandidate = ref(null);   // { valid, name, formatted, jid, number }
const phoneValidating = ref(false);
const phoneInput = ref('');          // raw cleaned phone
const activeTab = ref('message');     // 'message' | 'template'
const showTemplateModal = ref(false);

// SZ_GROUP_PILL_v2_20260522: modo "só canal" — usado quando o pill do remetente
// de grupo abre o modal porque NÃO achou conversa existente. Esconde a etapa 3
// (mensagem) e ao clicar num inbox cria conversa vazia + navega.
// v2b 20260522: se SzSenderBadge passou o contato via window.__szPrefillContact,
// pula direto pra etapa 2 (canal) — não mostra busca/contato. Modal vira 1 etapa.
const channelOnly = ref(!!window.__szChannelOnlyMode);
if (window.__szChannelOnlyMode) {
  // Já consumimos a flag — limpa pra próxima abertura ser modo normal
  try { delete window.__szChannelOnlyMode; } catch (e) { window.__szChannelOnlyMode = false; }
}
// Se contato veio pré-preenchido, pula direto pra etapa 2 + carrega inboxes
if (channelOnly.value && window.__szPrefillContact) {
  selectedContact.value = window.__szPrefillContact;
  step.value = 2;
  // fetchInboxes é definido mais abaixo no escopo; chama em microtask pra
  // garantir que tudo no setup foi inicializado.
  Promise.resolve().then(() => { try { fetchInboxes(); } catch (e) {} });
  try { delete window.__szPrefillContact; } catch (e) { window.__szPrefillContact = null; }
}

const isWhatsAppInbox = computed(() => {
  return selectedInbox.value?.channel_type === 'Channel::Whatsapp';
});

const whatsappTemplates = computed(() => {
  if (!selectedInbox.value?.id) return [];
  return store.getters['inboxes/getWhatsAppTemplates'](selectedInbox.value.id) || [];
});

const hasTemplates = computed(() => whatsappTemplates.value.length > 0);

const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUser = computed(() => store.getters.getCurrentUser);
const userToken = computed(() => currentUser.value?.access_token || '');

function close() { visible.value = false; emit('close'); }

function formatPhoneDisplay(raw) {
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length >= 10) return digits;
  return '';
}

function isPhoneInput(val) {
  const digits = val.replace(/[^\d+]/g, '');
  return digits.replace(/[^\d]/g, '').length >= 8;
}

function onSearchInput() {
  if (debounceTimer.value) clearTimeout(debounceTimer.value);
  phoneCandidate.value = null;
  const q = searchQuery.value?.trim() || '';
  if (!q || q.length < 2) { searchResults.value = []; return; }
  searchLoading.value = true;
  debounceTimer.value = setTimeout(async () => {
    await fetchContacts(q);
    // If no results and looks like phone, validate via Evo
    if (searchResults.value.length === 0 && isPhoneInput(q)) {
      phoneValidating.value = true;
      try {
        const resp = await fetch('/evo-api/validate-number', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: q }),
        });
        const data = await resp.json();
        phoneCandidate.value = data;
      } catch { phoneCandidate.value = { valid: false, error: 'Falha na validação' }; }
      phoneValidating.value = false;
    }
  }, 400);
}

async function fetchContacts(query) {
  try {
    const resp = await fetch(`/api/v1/accounts/${accountId.value}/search?q=${encodeURIComponent(query)}&include_contacts=true`, { headers: { api_access_token: userToken.value } });
    const data = await resp.json();
    searchResults.value = data.payload?.contacts || [];
  } catch { searchResults.value = []; }
  searchLoading.value = false;
}

function selectContact(contact) { selectedContact.value = contact; step.value = 2; fetchInboxes(); }

// SZ_GROUP_PILL_v2: em modo channelOnly, auto-avança da etapa 1 (contato)
// para etapa 2 (canal) assim que phoneCandidate.valid for true. Usuário não
// precisa clicar no card do telefone.
watch(phoneCandidate, (pc) => {
  if (!channelOnly.value) return;
  if (!pc?.valid) return;
  if (selectedContact.value) return;
  createContactFromPhone();
});

// SZ_GROUP_PILL_v2b: defense-in-depth — quando search retorna contatos e
// estamos em channelOnly, auto-seleciona o primeiro match (sem precisar
// clicar). Cobre o caso onde prefill da SzSenderBadge não preencheu por
// algum motivo (matching de phone diferente, etc).
watch(searchResults, (newResults) => {
  if (!channelOnly.value) return;
  if (selectedContact.value) return;
  if (!Array.isArray(newResults) || newResults.length === 0) return;
  selectContact(newResults[0]);
});

async function createContactFromPhone() {
  if (!phoneCandidate.value?.valid) return;
  const pc = phoneCandidate.value;
  searchLoading.value = true;
  try {
    const resp = await fetch('/api/v1/accounts/' + accountId.value + '/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', api_access_token: userToken.value },
      body: JSON.stringify({
        name: pc.name || pc.formatted,
        phone_number: pc.formatted,
        identifier: pc.jid || (pc.number + '@s.whatsapp.net'),
      }),
    });
    const data = await resp.json();
    let selected = null;
    if (data.payload?.contact) {
      selected = data.payload.contact;
    } else if (data.id) {
      selected = data;
    } else {
      // Contact likely exists (422) — find it by phone_number / identifier
      try {
        const sResp = await fetch(`/api/v1/accounts/${accountId.value}/contacts/search?q=${encodeURIComponent(pc.formatted || pc.number || '')}&include_contacts=true`, {
          headers: { api_access_token: userToken.value },
        });
        const sData = await sResp.json();
        selected = (sData.payload || []).find(c => c && (c.phone_number === pc.formatted || (c.identifier || '').includes(pc.number || '')));
      } catch (_) { /* ignore */ }
    }
    if (selected) selectContact(selected);
    else console.warn('[SzNewMsg] contact exists but not retrievable', data);
  } catch (e) { console.error('[SzNewMsg] create contact error', e); }
  searchLoading.value = false;
}

async function fetchInboxes() {
  try {
    const resp = await fetch(`/api/v1/accounts/${accountId.value}/inboxes`, { headers: { api_access_token: userToken.value } });
    const data = await resp.json();
    inboxes.value = data.payload || [];
  } catch { inboxes.value = []; }
}

function selectInbox(inbox) {
  selectedInbox.value = inbox;
  activeTab.value = 'message';
  // SZ_GROUP_PILL_v2: em modo channelOnly, não avança pra etapa de mensagem;
  // cria conversa vazia direto e navega.
  if (channelOnly.value) {
    createEmptyConversationAndNavigate(inbox);
    return;
  }
  step.value = 3;
  // Fetch inbox data to ensure templates are loaded
  if (inbox.channel_type === 'Channel::Whatsapp') {
    store.dispatch('inboxes/get', { id: inbox.id }).catch(() => {});
  }
}

async function createEmptyConversationAndNavigate(inbox) {
  if (!selectedContact.value || !inbox?.id) return;
  sending.value = true;
  try {
    const conv = await findOrCreateConversation(selectedContact.value.id, inbox.id);
    if (conv?.id) {
      close();
      const path = '/app/accounts/' + accountId.value + '/conversations/' + conv.id;
      const routerEl = document.querySelector('#app');
      const router = routerEl?.__vue_app__?.config?.globalProperties?.$router;
      if (router) {
        router.push(path).catch(() => { window.location.href = path; });
      } else {
        window.location.href = path;
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[SzNewMsg] empty conv create error', e);
  }
  sending.value = false;
}

async function sendTemplateMessage(messagePayload) {
  if (!selectedContact.value || !selectedInbox.value) return;
  sending.value = true;
  try {
    // Step 1: Find existing or create conversation
    const conv = await findOrCreateConversation(selectedContact.value.id, selectedInbox.value.id);
    if (conv.id) {
      // Step 2: Send template via store action (same route as ReplyBox)
      await store.dispatch('createPendingMessageAndSend', {
        conversationId: conv.id,
        ...messagePayload,
      });
      // Step 3: Navigate
      showTemplateModal.value = false;
      close();
      const path = '/app/accounts/' + accountId.value + '/conversations/' + conv.id;
      const routerEl = document.querySelector('#app');
      if (routerEl && routerEl.__vue_app__) {
        routerEl.__vue_app__.config.globalProperties.$router.push(path).catch(() => { window.location.href = path; });
      } else {
        window.location.href = path;
      }
    }
  } catch (e) { console.error('[SzNewMsg] template send error', e); }
  sending.value = false;
}

async function findOrCreateConversation(contactId, inboxId) {
  // Check existing open conversations via filter API
  try {
    const resp = await fetch('/api/v1/accounts/' + accountId.value + '/contacts/' + contactId + '/conversations', {
      headers: { api_access_token: userToken.value },
    });
    const data = await resp.json();
    const convs = data.payload || [];
    const existing = convs.find(cv => cv.inbox_id === inboxId && (cv.status === 'open' || cv.status === 'pending'));
    if (existing) return existing;
  } catch (e) { console.warn('[SzNewMsg] search existing conv error', e); }

  // Create new conversation
  const resp = await fetch('/api/v1/accounts/' + accountId.value + '/conversations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', api_access_token: userToken.value },
    body: JSON.stringify({ contact_id: contactId, inbox_id: inboxId }),
  });
  return await resp.json();
}

async function sendMessage() {
  if (!messageText.value.trim() || !selectedContact.value || !selectedInbox.value) return;
  sending.value = true;
  try {
    const conv = await findOrCreateConversation(selectedContact.value.id, selectedInbox.value.id);
    if (conv.id) {
      await fetch('/api/v1/accounts/' + accountId.value + '/conversations/' + conv.id + '/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', api_access_token: userToken.value },
        body: JSON.stringify({ content: messageText.value.trim() }),
      });
        close();
        // Navigate without page reload
        const path = '/app/accounts/' + accountId.value + '/conversations/' + conv.id;
        if (window.__vue_router__) {
          window.__vue_router__.push(path).catch(() => { window.location.href = path; });
        } else {
          // Use Chatwoot's internal navigation
          store.dispatch('setActiveChat', { data: conv, after: undefined });
          const routerEl = document.querySelector('#app');
          if (routerEl && routerEl.__vue_app__) {
            routerEl.__vue_app__.config.globalProperties.$router.push(path).catch(() => { window.location.href = path; });
          } else {
            window.location.href = path;
          }
        }
      }
  } catch (e) { console.error('[SzNewMsg]', e); }
  sending.value = false;
}

function getChannelIcon(type) {
  const map = {
    'Channel::Whatsapp': '📱',
    'Channel::WebWidget': '💬',
    'Channel::FacebookPage': '📘',
    'Channel::Telegram': '✈️',
    'Channel::Email': '📧',
    'Channel::Api': '🔗',
  };
  return map[type] || '📨';
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="sz-modal-backdrop" @click.self="close">
      <div class="sz-modal">
        <!-- Header -->
        <div class="sz-modal-header">
          <div class="sz-modal-header-left">
            <div class="sz-modal-header-badge">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            </div>
            <div class="sz-modal-header-text">
              <div class="sz-modal-title">{{ channelOnly ? 'Iniciar conversa' : 'Nova mensagem' }}</div>
              <div class="sz-modal-header-hint">{{ channelOnly ? 'Selecione o canal' : 'Busque um contato para iniciar' }}</div>
            </div>
          </div>
          <button class="sz-modal-close" @click="close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Stepper — escondido inteiro em channelOnly (modal vira 1 etapa) -->
        <div v-if="!channelOnly" class="sz-modal-stepper">
          <div class="sz-modal-step" :class="{ active: step === 1, done: step > 1 }">
            <div class="sz-modal-step-dot">{{ step > 1 ? '✓' : '1' }}</div>
            <span class="sz-modal-step-label">Contato</span>
          </div>
          <div class="sz-modal-step-line" :class="{ done: step > 1 }" />
          <div class="sz-modal-step" :class="{ active: step === 2, done: step > 2 }">
            <div class="sz-modal-step-dot">{{ step > 2 ? '✓' : '2' }}</div>
            <span class="sz-modal-step-label">Canal</span>
          </div>
          <div class="sz-modal-step-line" :class="{ done: step > 2 }" />
          <div class="sz-modal-step" :class="{ active: step === 3 }">
            <div class="sz-modal-step-dot">3</div>
            <span class="sz-modal-step-label">Mensagem</span>
          </div>
        </div>

        <!-- Body -->
        <div class="sz-modal-body">
          <!-- Step 1: Search -->
          <div v-if="step === 1" class="sz-modal-step-panel">
            <div class="sz-modal-search-wrap">
              <svg class="sz-modal-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              <input v-model="searchQuery" type="text" class="sz-modal-search-input" placeholder="Nome, telefone ou email..." @input="onSearchInput" />
            </div>
            <div v-if="searchLoading" class="sz-modal-results-hint">Buscando...</div>
            <div v-else-if="!searchQuery || searchQuery.length < 2" class="sz-modal-results-hint">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              Digite ao menos 2 caracteres para buscar
            </div>
            <div v-else class="sz-modal-results">
              <div v-for="c in searchResults" :key="c.id" class="sz-modal-contact-item" @click="selectContact(c)">
                <div class="sz-modal-contact-avatar">
                  <img v-if="c.thumbnail" :src="c.thumbnail" />
                  <span v-else>{{ (c.name || '?')[0].toUpperCase() }}</span>
                </div>
                <div class="sz-modal-contact-info">
                  <div class="sz-modal-contact-name">{{ c.name || 'Sem nome' }}</div>
                  <div class="sz-modal-contact-meta">{{ c.phone_number || c.email || '' }}</div>
                </div>
              </div>
              <div v-if="!searchResults.length && !phoneCandidate && !phoneValidating" class="sz-modal-results-hint">Nenhum contato encontrado</div>

              <!-- Phone validation loading -->
              <div v-if="phoneValidating" class="sz-modal-results-hint">
                <span class="sz-spinner-sm" /> Verificando número no WhatsApp...
              </div>

              <!-- Phone candidate found on WhatsApp -->
              <div v-if="phoneCandidate && phoneCandidate.valid && phoneCandidate.onWhatsApp && !searchResults.length" class="sz-modal-phone-card" @click="createContactFromPhone">
                <div class="sz-modal-phone-avatar">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg>
                </div>
                <div class="sz-modal-phone-info">
                  <div class="sz-modal-phone-name">{{ phoneCandidate.name || phoneCandidate.formatted }}</div>
                  <div class="sz-modal-phone-number">{{ phoneCandidate.formatted }}</div>
                </div>
                <div class="sz-modal-phone-badge">WhatsApp ✓</div>
              </div>

              <!-- Phone valid but not on WhatsApp — allow proceeding anyway -->
              <div v-if="phoneCandidate && phoneCandidate.valid && !phoneCandidate.onWhatsApp && !searchResults.length" class="sz-modal-phone-card sz-modal-phone-card--warn" @click="createContactFromPhone">
                <div class="sz-modal-phone-avatar sz-modal-phone-avatar--warn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <div class="sz-modal-phone-info">
                  <div class="sz-modal-phone-name">Número não localizado no WhatsApp</div>
                  <div class="sz-modal-phone-number">{{ phoneCandidate.formatted }} — clique para seguir mesmo assim</div>
                </div>
                <div class="sz-modal-phone-badge sz-modal-phone-badge--warn">Seguir →</div>
              </div>

              <!-- Phone format invalid -->
              <div v-if="phoneCandidate && !phoneCandidate.valid && !searchResults.length" class="sz-modal-results-hint sz-modal-phone-invalid">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                {{ phoneCandidate.error || 'Número inválido' }}
              </div>
            </div>
          </div>

          <!-- Step 2: Inbox -->
          <div v-if="step === 2" class="sz-modal-step-panel">
            <div class="sz-modal-selected-bar">
              <span>Para: <strong>{{ selectedContact?.name }}</strong></span>
            </div>
            <div class="sz-modal-inbox-list">
              <div v-for="inbox in inboxes" :key="inbox.id" class="sz-modal-inbox-item" @click="selectInbox(inbox)">
                <span class="sz-modal-inbox-icon">{{ getChannelIcon(inbox.channel_type) }}</span>
                <span>{{ inbox.name }}</span>
              </div>
              <div v-if="!inboxes.length" class="sz-modal-results-hint">Nenhuma caixa de entrada disponível</div>
            </div>
          </div>

          <!-- Step 3: Message -->
          <div v-if="step === 3" class="sz-modal-step-panel">
            <div class="sz-modal-selected-bar">
              <span>Para: <strong>{{ selectedContact?.name }}</strong> via <strong>{{ selectedInbox?.name }}</strong></span>
            </div>

            <!-- Tabs when WhatsApp -->
            <div v-if="isWhatsAppInbox && hasTemplates" class="sz-modal-tabs">
              <button
                class="sz-modal-tab"
                :class="{ active: activeTab === 'message' }"
                @click="activeTab = 'message'"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                Mensagem
              </button>
              <button
                class="sz-modal-tab"
                :class="{ active: activeTab === 'template' }"
                @click="activeTab = 'template'"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                Template
              </button>
            </div>

            <!-- Message tab -->
            <div v-if="activeTab === 'message'">
              <textarea v-model="messageText" class="sz-modal-textarea" placeholder="Escreva sua mensagem..." rows="4" />
              <div v-if="isWhatsAppInbox" class="sz-modal-hint">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                Mensagem livre só funciona dentro da janela de 24h. Use template para iniciar conversa.
              </div>
            </div>

            <!-- Template tab -->
            <div v-if="activeTab === 'template'" class="sz-modal-template-area">
              <button class="sz-modal-template-btn" @click="showTemplateModal = true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                Selecionar template
              </button>
              <p class="sz-modal-template-count">{{ whatsappTemplates.length }} templates disponíveis</p>
            </div>

            <!-- WhatsApp Templates Modal (reuses existing Chatwoot component) -->
            <WhatsappTemplatesModal
              v-if="showTemplateModal"
              :show="showTemplateModal"
              :inbox-id="selectedInbox?.id"
              @on-send="sendTemplateMessage"
              @cancel="showTemplateModal = false"
              @update:show="showTemplateModal = $event"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="sz-modal-footer">
          <button v-if="step > 1" class="sz-modal-btn-back" @click="step--">Voltar</button>
          <div v-else />
          <button v-if="step === 3 && activeTab === 'message'" class="sz-modal-btn-send" :disabled="!messageText.trim() || sending" @click="sendMessage">
            {{ sending ? 'Enviando...' : 'Enviar mensagem' }}
          </button>
          <button v-else-if="step === 1" class="sz-modal-btn-disabled" disabled>Selecione um contato</button>
          <button v-else class="sz-modal-btn-disabled" disabled>Selecione um canal</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.sz-modal-backdrop { position: fixed; inset: 0; z-index: 10050; background: rgba(0,0,0,0.35); display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
.sz-modal { background: #fff; border-radius: 16px; box-shadow: 0 24px 48px -12px rgba(0,0,0,0.2); width: 460px; max-width: calc(100vw - 32px); max-height: calc(100vh - 64px); display: flex; flex-direction: column; overflow: hidden; animation: sz-modal-enter 0.25s cubic-bezier(0.16,1,0.3,1); font-family: Inter, system-ui, sans-serif; }
@keyframes sz-modal-enter { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }

.sz-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 20px 14px; border-bottom: 1px solid #F4F4F5; }
.sz-modal-header-left { display: flex; align-items: center; gap: 12px; }
.sz-modal-header-badge { width: 34px; height: 34px; border-radius: 9px; background: #E8F5E9; display: flex; align-items: center; justify-content: center; color: #388E3C; flex-shrink: 0; }
.sz-modal-header-text { display: flex; flex-direction: column; gap: 1px; }
.sz-modal-title { font-size: 15px; font-weight: 600; color: #09090B; line-height: 1.3; }
.sz-modal-header-hint { font-size: 12px; color: #A1A1AA; line-height: 1.3; }
.sz-modal-close { width: 30px; height: 30px; border-radius: 8px; border: none; background: transparent; color: #A1A1AA; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.sz-modal-close:hover { background: #F4F4F5; color: #52525B; }

.sz-modal-stepper { display: flex; align-items: center; justify-content: center; gap: 0; padding: 14px 24px; border-bottom: 1px solid #F4F4F5; }
.sz-modal-step { display: flex; align-items: center; gap: 6px; opacity: 0.4; }
.sz-modal-step.active, .sz-modal-step.done { opacity: 1; }
.sz-modal-step-dot { width: 22px; height: 22px; border-radius: 50%; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; background: #E4E4E7; color: #71717A; }
.sz-modal-step.active .sz-modal-step-dot { background: #388E3C; color: #fff; }
.sz-modal-step.done .sz-modal-step-dot { background: #BBF7D0; color: #166534; }
.sz-modal-step-label { font-size: 12px; font-weight: 500; color: #71717A; }
.sz-modal-step.active .sz-modal-step-label { color: #09090B; }
.sz-modal-step.done .sz-modal-step-label { color: #388E3C; }
.sz-modal-step-line { width: 32px; height: 2px; background: #E4E4E7; margin: 0 8px; border-radius: 1px; }
.sz-modal-step-line.done { background: #86EFAC; }

.sz-modal-body { padding: 16px 20px; overflow-y: auto; flex: 1; }
.sz-modal-step-panel { animation: sz-step-in 0.2s ease-out; }
@keyframes sz-step-in { from { opacity: 0; transform: translateX(8px); } to { opacity: 1; transform: translateX(0); } }

.sz-modal-search-wrap { display: flex; align-items: center; gap: 10px; padding: 0 14px; height: 42px; border: 1.5px solid #E4E4E7; border-radius: 10px; background: #FAFAFA; margin-bottom: 12px; transition: border-color 0.15s; }
.sz-modal-search-wrap:focus-within { border-color: #388E3C; box-shadow: 0 0 0 3px rgba(56,142,60,0.08); background: #fff; }
.sz-modal-search-icon { flex-shrink: 0; color: #A1A1AA; }
.sz-modal-search-input { flex: 1; border: none !important; outline: none !important; background: transparent !important; box-shadow: none !important; padding: 0 !important; font-size: 13.5px; color: #09090B; height: auto !important; }
.sz-modal-search-input::placeholder { color: #A1A1AA; }

.sz-modal-results { overflow-y: auto; max-height: 280px; }
.sz-modal-results-hint { display: flex; align-items: center; justify-content: center; gap: 8px; color: #A1A1AA; font-size: 13px; padding: 28px 16px; text-align: center; }
.sz-modal-contact-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 10px; cursor: pointer; transition: background 0.1s; }
.sz-modal-contact-item:hover { background: #F4F4F5; }
.sz-modal-contact-avatar { width: 36px; height: 36px; border-radius: 50%; background: #E2E8F0; display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0; font-size: 14px; font-weight: 600; color: #64748B; }
.sz-modal-contact-avatar img { width: 100%; height: 100%; object-fit: cover; }
.sz-modal-contact-name { font-size: 14px; font-weight: 500; color: #09090B; }
.sz-modal-contact-meta { font-size: 12px; color: #A1A1AA; }

.sz-modal-selected-bar { padding: 8px 12px; background: #F4F4F5; border-radius: 8px; font-size: 13px; color: #52525B; margin-bottom: 12px; }
.sz-modal-inbox-list { display: flex; flex-direction: column; gap: 4px; }
.sz-modal-inbox-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border: 1px solid #E4E4E7; border-radius: 10px; cursor: pointer; font-size: 14px; }
.sz-modal-inbox-item:hover { border-color: #388E3C; background: rgba(56,142,60,0.04); }
.sz-modal-inbox-icon { font-size: 16px; }

.sz-modal-textarea { width: 100%; padding: 10px 12px; border: 1.5px solid #E4E4E7; border-radius: 10px; font-size: 14px; resize: vertical; outline: none; background: #FAFAFA; color: #09090B; box-sizing: border-box; font-family: inherit; }
.sz-modal-textarea:focus { border-color: #388E3C; background: #fff; }

.sz-modal-footer { display: flex; justify-content: space-between; align-items: center; padding: 12px 20px 16px; border-top: 1px solid #F4F4F5; }
.sz-modal-btn-back { padding: 8px 16px; border: 1px solid #E4E4E7; border-radius: 8px; background: #fff; font-size: 13px; cursor: pointer; color: #52525B; font-weight: 500; }
.sz-modal-btn-back:hover { background: #F4F4F5; }
.sz-modal-btn-send { padding: 8px 20px; border: none; border-radius: 8px; background: #388E3C; color: #fff; font-size: 13px; font-weight: 600; cursor: pointer; }
.sz-modal-btn-send:hover:not(:disabled) { background: #2E7D32; }
.sz-modal-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }
.sz-modal-btn-disabled { padding: 8px 16px; border: none; border-radius: 8px; background: #E4E4E7; color: #A1A1AA; font-size: 13px; font-weight: 500; cursor: not-allowed; }

/* Phone candidate */
.sz-modal-phone-card {
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 12px;
  background: linear-gradient(135deg, rgba(56,142,60,0.08), rgba(56,142,60,0.03));
  border: 1.5px solid rgba(56,142,60,0.25); cursor: pointer; transition: all 0.15s;
}
.sz-modal-phone-card:hover { border-color: #388E3C; background: rgba(56,142,60,0.12); }
.sz-modal-phone-avatar {
  width: 40px; height: 40px; border-radius: 50%; background: #E8F5E9;
  display: flex; align-items: center; justify-content: center; color: #388E3C; flex-shrink: 0;
}
.sz-modal-phone-info { flex: 1; display: flex; flex-direction: column; gap: 1px; }
.sz-modal-phone-name { font-size: 14px; font-weight: 600; color: #09090B; }
.sz-modal-phone-number { font-size: 12px; color: #52525B; }
.sz-modal-phone-badge {
  font-size: 11px; font-weight: 600; color: #388E3C; background: #E8F5E9;
  padding: 3px 8px; border-radius: 6px; white-space: nowrap;
}
.sz-modal-phone-invalid { color: #f87171 !important; }

/* "Number not on WhatsApp — proceed anyway" warning state */
.sz-modal-phone-card--warn { border-color: #F59E0B; background: rgba(245,158,11,0.05); }
.sz-modal-phone-card--warn:hover { border-color: #D97706; background: rgba(245,158,11,0.12); }
.sz-modal-phone-avatar--warn { background: #FEF3C7; color: #B45309; }
.sz-modal-phone-card--warn .sz-modal-phone-name { color: #92400E; }
.sz-modal-phone-badge--warn { color: #B45309; background: #FEF3C7; }
.dark .sz-modal-phone-card--warn { background: rgba(245,158,11,0.08); }
.dark .sz-modal-phone-avatar--warn { background: rgba(245,158,11,0.15); color: #FBBF24; }
.dark .sz-modal-phone-card--warn .sz-modal-phone-name { color: #FCD34D; }
.dark .sz-modal-phone-badge--warn { color: #FBBF24; background: rgba(245,158,11,0.15); }
.sz-spinner-sm {
  width: 14px; height: 14px; border: 2px solid #E4E4E7; border-top-color: #388E3C;
  border-radius: 50%; animation: sz-spin 0.6s linear infinite; display: inline-block;
}
@keyframes sz-spin { to { transform: rotate(360deg); } }

/* Tabs */
.sz-modal-tabs {
  display: flex; gap: 4px; margin-bottom: 12px;
  background: rgba(255,255,255,0.04); border-radius: 10px; padding: 3px;
}
.sz-modal-tab {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 12px; border: none; border-radius: 8px; background: transparent;
  font-size: 13px; font-weight: 500; color: #71717A; cursor: pointer;
  transition: all 0.15s; font-family: inherit;
}
.sz-modal-tab.active { background: rgba(56,142,60,0.12); color: #388E3C; }
.sz-modal-tab:hover:not(.active) { color: #09090B; }

.sz-modal-hint {
  display: flex; align-items: center; gap: 6px;
  margin-top: 8px; font-size: 11px; color: #A1A1AA; line-height: 1.4;
}

.sz-modal-template-area {
  display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 20px 0;
}
.sz-modal-template-btn {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 24px; border: 1.5px solid rgba(56,142,60,0.3); border-radius: 10px;
  background: rgba(56,142,60,0.06); color: #388E3C; font-size: 14px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; font-family: inherit;
}
.sz-modal-template-btn:hover { background: rgba(56,142,60,0.12); border-color: #388E3C; }
.sz-modal-template-count { font-size: 12px; color: #A1A1AA; }

/* Dark mode tabs */
.dark .sz-modal-tabs { background: rgba(255,255,255,0.04); }
.dark .sz-modal-tab { color: #6B6E75; }
.dark .sz-modal-tab.active { background: rgba(74,222,128,0.12); color: #4ADE80; }
.dark .sz-modal-tab:hover:not(.active) { color: #FAFAFA; }
.dark .sz-modal-hint { color: #6B6E75; }
.dark .sz-modal-template-btn { border-color: rgba(74,222,128,0.3); background: rgba(74,222,128,0.06); color: #4ADE80; }
.dark .sz-modal-template-btn:hover { background: rgba(74,222,128,0.12); border-color: #4ADE80; }
.dark .sz-modal-template-count { color: #6B6E75; }

/* Dark mode phone candidate */
.dark .sz-modal-phone-card { background: rgba(56,142,60,0.1); border-color: rgba(56,142,60,0.3); }
.dark .sz-modal-phone-card:hover { border-color: #4ADE80; }
.dark .sz-modal-phone-avatar { background: #1B3A1F; }
.dark .sz-modal-phone-name { color: #FAFAFA; }
.dark .sz-modal-phone-number { color: #9EA1A7; }
.dark .sz-modal-phone-badge { background: #1B3A1F; color: #4ADE80; }

/* Dark mode */
.dark .sz-modal { background: #1F2125; }
.dark .sz-modal-header { border-color: #363840; }
.dark .sz-modal-title { color: #FAFAFA; }
.dark .sz-modal-header-hint { color: #6B6E75; }
.dark .sz-modal-header-badge { background: #1B3A1F; }
.dark .sz-modal-stepper { border-color: #363840; }
.dark .sz-modal-step-dot { background: #363840; color: #9EA1A7; }
.dark .sz-modal-step-label { color: #9EA1A7; }
.dark .sz-modal-step.active .sz-modal-step-label { color: #FAFAFA; }
.dark .sz-modal-step-line { background: #363840; }
.dark .sz-modal-search-wrap { border-color: #363840; background: #282A2E; }
.dark .sz-modal-search-wrap:focus-within { border-color: #4ADE80; background: #1F2125; }
.dark .sz-modal-search-input { color: #FAFAFA; }
.dark .sz-modal-search-input::placeholder { color: #6B6E75; }
.dark .sz-modal-results-hint { color: #6B6E75; }
.dark .sz-modal-contact-item:hover { background: #282A2E; }
.dark .sz-modal-contact-name { color: #FAFAFA; }
.dark .sz-modal-contact-meta { color: #6B6E75; }
.dark .sz-modal-contact-avatar { background: #363840; color: #9EA1A7; }
.dark .sz-modal-selected-bar { background: #282A2E; color: #9EA1A7; }
.dark .sz-modal-inbox-item { border-color: #363840; color: #FAFAFA; }
.dark .sz-modal-inbox-item:hover { border-color: #4ADE80; }
.dark .sz-modal-textarea { border-color: #363840; background: #282A2E; color: #FAFAFA; }
.dark .sz-modal-textarea:focus { border-color: #4ADE80; background: #1F2125; }
.dark .sz-modal-footer { border-color: #363840; }
.dark .sz-modal-btn-back { border-color: #363840; color: #9EA1A7; background: transparent; }
.dark .sz-modal-btn-disabled { background: #363840; color: #6B6E75; }
.dark .sz-modal-close { color: #6B6E75; }
.dark .sz-modal-close:hover { background: #363840; color: #FAFAFA; }
</style>
